// features/invite/invite.slice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { submitInviteApi, sendInviteEmailApi, getUserReferralApi } from './invite.service';

// Async Thunks
export const submitInvite = createAsyncThunk(
    'invite/submitInvite',
    async (emails: string[]) => {
        const data = await submitInviteApi(emails);
        return data;
    }
);

export const sendInviteEmail = createAsyncThunk(
    'invite/sendInviteEmail',
    async ({ email, lang }: { email: string; lang?: string }, { rejectWithValue }) => {
        try {
            const data = await sendInviteEmailApi(email, lang);
            return data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const fetchUserReferral = createAsyncThunk(
    'invite/fetchUserReferral',
    async (lang:string, { rejectWithValue }) => {
        try {
            const data = await getUserReferralApi(lang);
            return data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

type Awaited<T> = T extends Promise<infer U> ? U : T;

type InviteState = {
    message: string | null;
    inviteData: Awaited<ReturnType<typeof submitInviteApi>> | null;
    emailInviteData: Awaited<ReturnType<typeof sendInviteEmailApi>> | null;
    referralData: Awaited<ReturnType<typeof getUserReferralApi>> | null;
    loading: boolean;
    referralLoading: boolean;
    error: string | null;
    referralError: string | null;
    success: boolean;
};

const initialState: InviteState = {
    message: null,
    inviteData: null,
    emailInviteData: null,
    referralData: null,
    loading: false,
    referralLoading: false,
    error: null,
    referralError: null,
    success: false,
};

const inviteSlice = createSlice({
    name: 'invite',
    initialState,
    reducers: {
        clearInviteState: (state) => {
            state.inviteData = null;
            state.emailInviteData = null;
            state.referralData = null;
            state.error = null;
            state.referralError = null;
            state.success = false;
            state.message = null;
        },
        clearInviteData: (state) => {
            state.inviteData = null;
            state.emailInviteData = null;
            state.error = null;
            state.message = null;
        },
        resetInviteStatus: (state) => {
            state.success = false;
            state.error = null;
        },
        clearReferralError: (state) => {
            state.referralError = null;
        },
    },
    extraReducers: builder => {
        builder
            // Submit Invite Actions
            .addCase(submitInvite.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(submitInvite.fulfilled, (state, action) => {
                state.loading = false;
                state.inviteData = action.payload;
                state.message = 'Invitations sent successfully!';
                state.success = true;
            })
            .addCase(submitInvite.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                
                // Try to extract the error message from the API response
                let errorMessage = 'Failed to send invitations';
                
                if (action.error.message) {
                    try {
                        // Try to parse JSON error response
                        const errorData = JSON.parse(action.error.message);
                        if (errorData.message) {
                            errorMessage = errorData.message;
                        }
                    } catch {
                        // If not JSON, use the raw error message
                        errorMessage = action.error.message;
                    }
                }
                
                state.error = errorMessage;
            })
            // Send Invite Email Actions
            .addCase(sendInviteEmail.pending, state => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(sendInviteEmail.fulfilled, (state, action) => {
                state.loading = false;
                state.emailInviteData = action.payload;
                state.success = true;
            })
            .addCase(sendInviteEmail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to send invite email';
                state.success = false;
            })
            // Fetch User Referral Actions
            .addCase(fetchUserReferral.pending, state => {
                state.referralLoading = true;
                state.referralError = null;
            })
            .addCase(fetchUserReferral.fulfilled, (state, action) => {
                state.referralLoading = false;
                state.referralData = action.payload;
            })
            .addCase(fetchUserReferral.rejected, (state, action) => {
                state.referralLoading = false;
                state.referralError = action.error.message || 'Failed to fetch referral data';
            });
    },
});

export const { clearInviteState, clearInviteData, resetInviteStatus, clearReferralError } = inviteSlice.actions;
export default inviteSlice.reducer;
